(() => {
  if (customElements.get('luminova-header')) return;
  class LuminovaHeader extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.section = this.closest('.section-header') || this.parentElement;
      this.toggle = this.querySelector('[data-lh-toggle]');
      this.panel = this.querySelector('.lh-mobile');
      this.bar = this.querySelector('.lh-bar');
      this.desktop = window.matchMedia('(min-width: 990px)');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.dropdowns = [...this.querySelectorAll('[data-lh-desktop]')];
      this.scrollY = window.scrollY;
      this.lastScroll = this.scrollY;
      const on = (node, name, fn, options = {}) => node?.addEventListener(name, fn, { ...options, signal:this.abort.signal });
      on(this.toggle, 'click', () => this.mobileOpen ? this.closeMobile() : this.openMobile());
      this.dropdowns.forEach(details => {
        on(details, 'pointerenter', event => { if (this.desktop.matches && event.pointerType === 'mouse') { clearTimeout(this.leaveTimer); this.openDesktop(details); } });
        on(details, 'pointerleave', () => { this.leaveTimer = setTimeout(() => { if (!details.contains(document.activeElement)) this.closeDesktop(); }, 160); });
        on(details, 'toggle', () => {
          details.querySelector('summary').setAttribute('aria-expanded', String(details.open));
          if (details.open) this.openDesktop(details);
          this.section.classList.toggle('lh-section--open', this.dropdowns.some(item => item.open));
        });
        on(details.querySelector('summary'), 'keydown', event => {
          if (event.key === 'ArrowDown') { event.preventDefault(); this.openDesktop(details); details.querySelector('.lh-mega a')?.focus(); }
        });
      });
      this.querySelectorAll('[data-lh-accordion]').forEach(details => on(details, 'toggle', () => {
        details.querySelector('summary').setAttribute('aria-expanded', String(details.open));
        if (details.open) {
          const list = details.parentElement.parentElement;
          [...list.children].forEach(li => { const sibling = li.querySelector(':scope > details'); if (sibling && sibling !== details) sibling.open = false; });
        }
      }));
      on(this, 'keydown', event => this.onKey(event));
      on(document, 'pointerdown', event => { if (!this.contains(event.target)) this.closeDesktop(); });
      on(this, 'focusout', () => { requestAnimationFrame(() => { if (!this.contains(document.activeElement)) this.closeDesktop(); }); });
      // Release our focus/scroll lock before Dawn opens its own search or cart dialog.
      on(this, 'click', event => {
        if (event.target.closest('.lh-search summary, .header__icon--cart, .lh-tools a')) {
          this.closeMobile(false); this.closeDesktop();
        }
      }, { capture:true });
      on(this.desktop, 'change', () => { this.closeMobile(false); this.closeDesktop(); this.measure(); });
      on(window, 'scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => { this.scrollFrame = null; this.onScroll(); });
      }, { passive:true });
      on(document, 'shopify:block:select', event => {
        const block = this.querySelector(`[data-lh-feature-id="${CSS.escape(event.detail.blockId)}"]`);
        if (block && this.desktop.matches) this.openDesktop(block.closest('[data-lh-desktop]'));
      });
      this.resize = new ResizeObserver(() => this.measure());
      this.resize.observe(this.bar);
      this.resize.observe(this.querySelector('.lh-tools'));
      this.resize.observe(this);
      this.measure();
      this.messages = [...this.querySelectorAll('[data-lh-message]')];
      this.pause = this.querySelector('[data-lh-pause]');
      this.paused = this.motion.matches;
      this.updatePause();
      on(this.pause, 'click', () => { this.paused = !this.paused; this.updatePause(); });
      on(this.motion, 'change', () => { this.paused = this.motion.matches; this.updatePause(); });
      if (this.messages.length > 1) this.messageTimer = setInterval(() => {
        if (this.paused || this.mobileOpen || document.hidden || this.querySelector('.lh-announcement')?.matches(':hover, :focus-within')) return;
        const index = this.messages.findIndex(message => !message.hidden);
        this.messages.forEach((message, i) => { message.hidden = i !== (index + 1) % this.messages.length; });
      }, 5000);
    }
    measure() {
      this.style.setProperty('--lh-tools-width', `${this.querySelector('.lh-tools').getBoundingClientRect().width}px`);
      this.style.setProperty('--lh-bar-height', `${this.bar.getBoundingClientRect().height}px`);
      if (!this.mobileOpen) {
        const height = this.offsetHeight;
        document.documentElement.style.setProperty('--header-height', `${height}px`);
        this.section.style.setProperty('--lh-sticky-height', `${height}px`);
        this.updatePinnedHeader();
      }
    }
    updatePinnedHeader() {
      if (this.mobileOpen) return;
      const enabled = this.dataset.sticky === 'always' || this.dataset.sticky === 'reduce-logo-size';
      // Keep the section in normal flow. Body overflow can prevent CSS sticky
      // from following the viewport, so pin only the visible header inside it.
      this.section.classList.toggle('lh-section--fixed-host', enabled);
      this.section.classList.toggle('lh-section--pinned', enabled && this.section.getBoundingClientRect().top <= 0);
      if (enabled || this.dataset.sticky === 'none') {
        this.section.classList.remove('shopify-section-header-sticky','shopify-section-header-hidden','animate');
      }
    }
    updatePause() {
      if (!this.pause) return;
      this.pause.setAttribute('aria-pressed', String(this.paused));
      this.pause.setAttribute('aria-label', this.paused ? 'Play announcements' : 'Pause announcements');
      this.pause.textContent = this.paused ? '▷' : 'Ⅱ';
    }
    openDesktop(details) {
      if (!details) return;
      clearTimeout(this.leaveTimer);
      this.dropdowns.forEach(item => { if (item !== details) item.open = false; });
      details.open = true;
      this.section.classList.add('lh-section--open');
    }
    closeDesktop() {
      clearTimeout(this.leaveTimer);
      this.dropdowns.forEach(item => { item.open = false; });
      this.section?.classList.remove('lh-section--open');
    }
    openMobile() {
      if (this.desktop.matches || this.mobileOpen) return;
      this.mobileOpen = true;
      this.closeDesktop();
      this.savedFocus = document.activeElement;
      this.scrollY = window.scrollY;
      this.bodyStyle = {};
      ['position','top','width','overflow'].forEach(key => { this.bodyStyle[key] = document.body.style[key]; });
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      this.section.classList.add('lh-section--mobile');
      this.classList.add('lh--drawer-open');
      this.panel.hidden = false;
      this.panel.scrollTop = 0;
      this.toggle.setAttribute('aria-expanded','true');
      this.toggle.setAttribute('aria-label',this.toggle.dataset.closeLabel);
      this.setAttribute('role','dialog');
      this.setAttribute('aria-modal','true');
      this.setAttribute('aria-label',this.toggle.dataset.openLabel);
      this.inertNodes = [];
      for (let branch = this; branch.parentElement && branch !== document.body; branch = branch.parentElement) {
        [...branch.parentElement.children].forEach(sibling => {
          if (sibling !== branch && !['SCRIPT','STYLE','LINK'].includes(sibling.tagName) && !sibling.inert) {
            sibling.inert = true; this.inertNodes.push(sibling);
          }
        });
      }
      this.toggle.focus({ preventScroll:true });
      this.measure();
    }
    closeMobile(restoreFocus = true) {
      if (!this.mobileOpen) return;
      this.mobileOpen = false;
      this.panel.hidden = true;
      this.classList.remove('lh--drawer-open');
      this.section.classList.remove('lh-section--mobile');
      this.toggle.setAttribute('aria-expanded','false');
      this.toggle.setAttribute('aria-label',this.toggle.dataset.openLabel);
      ['role','aria-modal','aria-label'].forEach(key => this.removeAttribute(key));
      this.inertNodes?.forEach(node => { node.inert = false; });
      Object.assign(document.body.style, this.bodyStyle);
      const originalBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = 'auto';
      window.scrollTo(0,this.scrollY);
      document.documentElement.style.scrollBehavior = originalBehavior;
      this.lastScroll = this.scrollY;
      if (restoreFocus && this.savedFocus?.isConnected) this.savedFocus.focus({ preventScroll:true });
      this.measure();
    }
    onKey(event) {
      if (event.key === 'Escape') {
        if (this.mobileOpen) { event.preventDefault(); event.stopPropagation(); this.closeMobile(); return; }
        const opened = this.dropdowns.find(item => item.open);
        if (opened) { event.preventDefault(); this.closeDesktop(); opened.querySelector('summary').focus(); }
      }
      if (event.key !== 'Tab' || !this.mobileOpen) return;
      const items = [...this.querySelectorAll('a[href],button:not([disabled]),summary,input,select,[tabindex="0"]')].filter(item => item.getClientRects().length && !item.closest('[inert]'));
      const first = items[0], last = items.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
    onScroll() {
      if (this.mobileOpen) return;
      const y = window.scrollY, delta = y - this.lastScroll;
      this.lastScroll = y;
      this.updatePinnedHeader();
      if (this.dataset.sticky === 'none') return;
      const past = y > this.section.offsetHeight;
      this.section.classList.toggle('scrolled-past-header',past);
      if (this.dataset.sticky !== 'on-scroll-up') return;
      this.section.classList.toggle('shopify-section-header-sticky',past);
      if (!past) { this.section.classList.remove('shopify-section-header-hidden','animate'); return; }
      if (this.preventHide || (this.contains(document.activeElement) && document.activeElement.matches(':focus-visible')) || this.querySelector('details-modal details[open]')) {
        this.section.classList.remove('shopify-section-header-hidden'); return;
      }
      if (delta > 3) { this.section.classList.add('shopify-section-header-hidden'); this.closeDesktop(); }
      else if (delta < -3) { this.section.classList.remove('shopify-section-header-hidden'); this.section.classList.add('animate'); }
    }
    disconnectedCallback() {
      this.closeMobile(false);
      this.closeDesktop();
      this.abort?.abort();
      this.resize?.disconnect();
      clearInterval(this.messageTimer);
      clearTimeout(this.leaveTimer);
      cancelAnimationFrame(this.scrollFrame);
      this.section?.classList.remove('shopify-section-header-sticky','shopify-section-header-hidden','scrolled-past-header','animate');
      this.section?.classList.remove('lh-section--fixed-host','lh-section--pinned');
      this.section?.style.removeProperty('--lh-sticky-height');
    }
  }
  customElements.define('luminova-header', LuminovaHeader);
})();
